import { __exportAll } from "./_virtual/_rolldown/runtime.js";
import { CountryCode } from "@biteinc/enums";
//#region src/validators.ts
var validators_exports = /* @__PURE__ */ __exportAll({
	addressLineRegex: () => addressLineRegex,
	barcodeRegexString: () => barcodeRegexString,
	deliveryAddressCityValidationRegex: () => deliveryAddressCityValidationRegex,
	deliveryAddressPostalCodeValidationRegex: () => deliveryAddressPostalCodeValidationRegex,
	deliveryAddressStateValidationRegex: () => deliveryAddressStateValidationRegex,
	deliveryInstructionsValidationRegex: () => deliveryInstructionsValidationRegex,
	emailRegex: () => emailRegex,
	generalAvailabilityBuildNameRegexString: () => generalAvailabilityBuildNameRegexString,
	getTokenMatchesFromStringSpec: () => getTokenMatchesFromStringSpec,
	guestNameValidationRegex: () => guestNameValidationRegex,
	guestVehicleValidationRegex: () => guestVehicleValidationRegex,
	hexColorWithOptionalOpacityRegexString: () => hexColorWithOptionalOpacityRegexString,
	ipAddressRegexString: () => ipAddressRegexString,
	ipAddressWithPortRegexString: () => ipAddressWithPortRegexString,
	isEmail: () => isEmail,
	isPhone: () => isPhone,
	normalizePostalCode: () => normalizePostalCode,
	outpostRoomNumberValidationRegex: () => outpostRoomNumberValidationRegex,
	passwordValidationRegex: () => passwordValidationRegex,
	postalCodeRegexString: () => postalCodeRegexString,
	releasableBuildNameRegexString: () => releasableBuildNameRegexString,
	roomNumberRegexString: () => roomNumberRegexString,
	simpleStringRegexString: () => simpleStringRegexString,
	siteStoreNumberRegexString: () => siteStoreNumberRegexString,
	smsRegexString: () => smsRegexString,
	tableNumberValidationRegex: () => tableNumberValidationRegex,
	tokenMatchesAreValid: () => tokenMatchesAreValid,
	vistaSchemaVersionRegexString: () => vistaSchemaVersionRegexString
});
const barcodeRegexString = "^[\\x20-\\x7E]+$";
const hexColorWithOptionalOpacityRegexString = "^#([A-F0-9]{6})([A-F0-9]{2}){0,1}$";
function simpleStringRegexString(options = {}) {
	const { maxLength, specialCharacters = "", inverse = false, allowCommas = false, allowLineBreaks = false, rejectEmptySpace = false, allowDiacritics = false } = options;
	const lengthRestriction = maxLength ? `{1,${maxLength}}` : "+";
	const commasAndSpecialChars = allowCommas ? `,${specialCharacters}` : specialCharacters;
	const lineBreaks = allowLineBreaks ? "\\r\\n" : "";
	if (rejectEmptySpace) {
		if (inverse) throw new Error("Regex inverse option is not supported with rejectEmptySpace option.");
		const baseRegex = `[${`a-zA-Z0-9${allowDiacritics ? "À-ž" : ""} \\-\\.${commasAndSpecialChars}${lineBreaks}`}]+`;
		return `^(?=.*[^\\s\\-\\.])${maxLength ? `(?=^.{1,${maxLength}}$)` : ""}${baseRegex}$`;
	}
	const regexBody = `a-zA-Z0-9${allowDiacritics ? "À-ž" : ""} \\-\\.${commasAndSpecialChars}${lineBreaks}`;
	return inverse ? `[^${regexBody}]` : `^[${regexBody}]${lengthRestriction}$`;
}
const ipAddressRegexString = "^\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}$";
const ipAddressWithPortRegexString = "^\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}:\\d{1,5}$";
const vistaSchemaVersionRegexString = "^\\d{2}\\.\\d{2}\\.\\d{2}$";
function roomNumberRegexString() {
	return simpleStringRegexString({
		maxLength: 10,
		rejectEmptySpace: true
	});
}
const addressLineRegex = simpleStringRegexString({
	specialCharacters: `:&#,'"()+/`,
	allowDiacritics: true
});
const siteStoreNumberRegexString = "^[A-Z0-9-]+$";
const postalCodeRegexString = "^(\\d{5}|([ABCEGHJ-NPRSTVXY]\\d[ABCEGHJ-NPRSTV-Z]\\s?\\d[ABCEGHJ-NPRSTV-Z]\\d)|([A-Za-z]{1,2}\\d[A-Za-z\\d]?\\s?\\d[A-Za-z]{2}))$";
/**
* Validate and canonicalize a postal code for a specific country.
*
* Deliberately NOT input-equivalent to `postalCodeRegexString` above: that one is
* country-agnostic and rejects the Canadian hyphen and lowercase forms this accepts. The
* relationship that does hold is on the output — every value returned here satisfies
* `postalCodeRegexString`.
*
* The switch is total over `CountryCode` with no `default`, so adding a country is a
* compile error here rather than a silently-unvalidated code path.
*/
function normalizePostalCode(countryCode, postalCode) {
	switch (countryCode) {
		case CountryCode.Canada: {
			const matches = postalCode.toUpperCase().match(/^([ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z])[ -]?(\d[ABCEGHJ-NPRSTV-Z]\d)$/i);
			if (matches) return {
				ok: true,
				normalizedPostalCode: `${matches[1]} ${matches[2]}`
			};
			return {
				ok: false,
				message: "Please enter a valid Canadian postal code (e.g. K1M 1M4)"
			};
		}
		case CountryCode.UnitedStates:
			if (postalCode.match(/^\d{5}$/)) return {
				ok: true,
				normalizedPostalCode: postalCode
			};
			return {
				ok: false,
				message: "Please enter a valid US ZIP code (e.g. 12345)"
			};
		case CountryCode.Germany:
			if (postalCode.match(/^\d{5}$/)) return {
				ok: true,
				normalizedPostalCode: postalCode
			};
			return {
				ok: false,
				message: "Please enter a valid German postal code (e.g. 10115)"
			};
		case CountryCode.UnitedKingdom: {
			const matches = postalCode.toUpperCase().match(/^([A-Z]{1,2}\d[A-Z\d]?)\s?(\d[A-Z]{2})$/i);
			if (matches) return {
				ok: true,
				normalizedPostalCode: `${matches[1]} ${matches[2]}`
			};
			return {
				ok: false,
				message: "Please enter a valid UK postcode (e.g. SW1A 1AA)"
			};
		}
	}
}
const smsRegexString = "^[\\sa-zA-Z0-9\\!\\@\\#\\$\\%\\^\\&\\*\\(\\)\\,\\.\\/\\<\\>\\?\\;\\'\\:\\\"\\[\\]\\{\\}\\-\\+\\_\\=\\~]{0,160}$";
const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const passwordValidationRegex = "^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z]).{14,32}$";
const releasableBuildNameRegexString = "^biteinc-(chromeos|garcon|gcn|vitrine)\\.([0-9]|[1-9][0-9]*)\\.([0-9]|[1-9][0-9]*)\\.([0-9]|[1-9][0-9]*)(-([0-9A-Za-z-]+)\\.([0-9]|[1-9][0-9]*)){0,1}$";
const generalAvailabilityBuildNameRegexString = /^biteinc-(chromeos|garcon|gcn|vitrine).[0-9]+.[0-9]+.[0-9]+$/;
function getTokenMatchesFromStringSpec(stringSpec, languageCode) {
	return (stringSpec[languageCode] || "").match(/\{\{\$[0-9].*?\}\}/g) || [];
}
function isEmail(input) {
	return emailRegex.test(input || "");
}
function tokenMatchesAreValid(tokenMatches) {
	for (let i = 0; i < tokenMatches.length; i++) if (!tokenMatches.some((match) => {
		return match.startsWith(`{{$${i + 1}`);
	})) return false;
	return true;
}
const PHONE_NUMBER_LENGTH = 10;
const numberRegex = /^\d+$/;
const consecutiveNumbersRegex = /^((?:0(?=1|$))?(?:1(?=2|$))?(?:2(?=3|$))?(?:3(?=4|$))?(?:4(?=5|$))?(?:5(?=6|$))?(?:6(?=7|$))?(?:7(?=8|$))?(?:8(?=9|$))?9?|(?:9(?=8|$))?(?:8(?=7|$))?(?:7(?=6|$))?(?:6(?=5|$))?(?:5(?=4|$))?(?:4(?=3|$))?(?:3(?=2|$))?(?:2(?=1|$))?(?:1(?=0|$))?0?)$/;
const repeatedNumbersRegex = /\b(\d)\1+\b/;
const validAreaCodeRegex = /^([2-9][0-8][0-9])$/;
function isPhone(value) {
	return !(!value || value.length < PHONE_NUMBER_LENGTH || value.length > PHONE_NUMBER_LENGTH || consecutiveNumbersRegex.test(value) || repeatedNumbersRegex.test(value) || !validAreaCodeRegex.test(value.slice(0, 3)) || !numberRegex.test(value));
}
const deliveryAddressCityValidationRegex = simpleStringRegexString({ allowDiacritics: true });
const deliveryAddressStateValidationRegex = simpleStringRegexString();
const deliveryAddressPostalCodeValidationRegex = postalCodeRegexString;
const deliveryInstructionsValidationRegex = simpleStringRegexString({
	allowCommas: true,
	allowLineBreaks: true
});
const guestNameValidationRegex = simpleStringRegexString({
	rejectEmptySpace: true,
	maxLength: 30,
	allowDiacritics: true
});
const guestVehicleValidationRegex = simpleStringRegexString({ allowDiacritics: true });
const outpostRoomNumberValidationRegex = roomNumberRegexString();
const tableNumberValidationRegex = simpleStringRegexString({ rejectEmptySpace: true });
//#endregion
export { addressLineRegex, barcodeRegexString, deliveryAddressCityValidationRegex, deliveryAddressPostalCodeValidationRegex, deliveryAddressStateValidationRegex, deliveryInstructionsValidationRegex, emailRegex, generalAvailabilityBuildNameRegexString, getTokenMatchesFromStringSpec, guestNameValidationRegex, guestVehicleValidationRegex, hexColorWithOptionalOpacityRegexString, ipAddressRegexString, ipAddressWithPortRegexString, isEmail, isPhone, normalizePostalCode, outpostRoomNumberValidationRegex, passwordValidationRegex, postalCodeRegexString, releasableBuildNameRegexString, roomNumberRegexString, simpleStringRegexString, siteStoreNumberRegexString, smsRegexString, tableNumberValidationRegex, tokenMatchesAreValid, validators_exports, vistaSchemaVersionRegexString };

//# sourceMappingURL=validators.js.map